/** The taxonomy cell. A stale FK (curriculum re-imported since tagging) still shows its old label
 *  plus a hint; `curriculum_topic_id: null` renders an explicit "No topic", never an empty cell. */
import type { AssignmentTemplateListRow } from './interfaces'

import { STALE_HINT, getTaxonomyLabel, getTaxonomyShortLabel } from './assignment-template-options'

const UNNAMED_TOPIC_LABEL = 'Unnamed topic'

interface UseAssignmentTaxonomyOptions {
  template: ComputedRef<AssignmentTemplateListRow>
}

interface AssignmentTaxonomyReturn {
  /** The bold `M.4 L.3` line. */
  refShortLabel: ComputedRef<string | null>
  /** What the cell prints for a tagged row: the short label, else the full one, else a stand-in —
   *  a row with a topic id but no labels at all is still tagged, never "No topic". */
  refDisplayLabel: ComputedRef<string>
  /** The lesson title under it, or `null` when the row has no lesson. */
  refLessonTitle: ComputedRef<string | null>
  /** The full `module › topic › lesson` label, shown as the cell's tooltip. */
  refLabel: ComputedRef<string | null>
  refHasTopic: ComputedRef<boolean>
  refIsStale: ComputedRef<boolean>
  staleHint: string
}

export const useAssignmentTaxonomy = (
  { template }: UseAssignmentTaxonomyOptions,
): AssignmentTaxonomyReturn => {
  const refShortLabel = computed<string | null>(() => getTaxonomyShortLabel(template.value))
  const refLessonTitle = computed<string | null>(() =>
    template.value.curriculum_lesson_id === null ? null : template.value.lesson_name || null)
  const refLabel = computed<string | null>(() => getTaxonomyLabel(template.value))
  const refDisplayLabel = computed<string>(() => refShortLabel.value ?? refLabel.value ?? UNNAMED_TOPIC_LABEL)
  const refHasTopic = computed<boolean>(() => template.value.curriculum_topic_id !== null)

  // Read off the row, never re-derived: the list filter uses the same server-side rule.
  const refIsStale = computed<boolean>(() => template.value.is_stale)

  return {
    refShortLabel,
    refDisplayLabel,
    refLessonTitle,
    refLabel,
    refHasTopic,
    refIsStale,
    staleHint: STALE_HINT,
  }
}
